import { Box, Card, CardContent, styled } from '@mui/material';
import { useLoader } from '@/contexts/loader/LoaderContext';
import { useContext, useEffect } from 'react';
import { DateFormats, useFormatting } from '@/utils/formatting';
import { UserContext } from '@/contexts/user/UserContext';
import dayjs from 'dayjs';
import { useCurrentCompanyDetails } from '@/services/paymentsQuery';
import {
    PageSubHeading,
    TextDisplaySMMedium,
    TextLGRegular,
    TextLGRegularBrand,
    TextLGSemiBoldBrand
} from '@/typography/Typography';

const StyledCard = styled(Card)`
    display: flex;
    height: 222px;
    margin-top: 24px;
    margin-bottom: 24px;
    padding: 24px 16px;
    flex-direction: column;
    align-items: center;
    gap: 2px;
    align-self: stretch;
    border-radius: 8px;
    background: var(--Brand-600, #1570ef);
    /* Shadow/sm */
    box-shadow: 0px 1px 3px 0px rgba(0, 0, 0, 0.4);
`;
const StyledCardContent = styled(CardContent)`
    display: flex;
    padding: 16px;
    align-items: center;
    gap: 32px;
    justify-content: space-between;
    align-self: stretch;
`;

const VerticalDivider = styled('div')`
    width: 1px;
    background: #fff;
    height: 80px;
`;

export const SubscriptionDetails = () => {
    const { setLoading } = useLoader();
    const { formatDate, getBalanceLabel } = useFormatting();

    // Shares the cached companyDetails read with the rest of the page instead of
    // fetching the same endpoint again into local state.
    const { data: companyDetails, isPending } = useCurrentCompanyDetails();
    const company = companyDetails?.data;

    // setLoading is a counter (+1 / -1), not a flag, so every increment needs a
    // matching decrement. Raising it on entry and releasing it in cleanup keeps
    // the count balanced; calling setLoading(isPending) directly would leave it
    // stuck at 1 for as long as the query stays pending.
    useEffect(() => {
        if (!isPending) {
            return;
        }
        setLoading(true);
        return () => setLoading(false);
    }, [isPending, setLoading]);

    const { company: userCompany } = useContext(UserContext);

    const getNextPeriodStartIfNoTrial = () => {
        return dayjs()
            .add(1, 'month')
            .startOf('month')
            .format(DateFormats.DATE_WITH_SHORT_MONTH);
    };

    const getNextPeriodStart = () => {
        if (userCompany?.endOfTrial) {
            let nextPeriodStart = dayjs(userCompany?.endOfTrial)
                .add(1, 'month')
                .startOf('month');

            if (nextPeriodStart.isAfter(dayjs())) {
                var nextPeriodStartStr = nextPeriodStart.format(
                    DateFormats.DATE_WITH_SHORT_MONTH
                );
                return nextPeriodStartStr;
            }
        }
        return getNextPeriodStartIfNoTrial();
    };

    return (
        <>
            {userCompany?.endOfTrial && (
                <PageSubHeading>
                    Free trial ending :{' '}
                    {formatDate(
                        userCompany?.endOfTrial,
                        DateFormats.DATE_WITH_SHORT_MONTH
                    )}
                </PageSubHeading>
            )}
            <Box>
                <StyledCard>
                    <TextLGSemiBoldBrand alignSelf={'start'}>
                        Plan
                    </TextLGSemiBoldBrand>
                    <StyledCardContent>
                        <div style={{ marginRight: 'auto' }}>
                            <TextDisplaySMMedium>
                                InstaPaid Basic
                            </TextDisplaySMMedium>
                            <TextLGRegularBrand>
                                Monthly subscription
                            </TextLGRegularBrand>
                        </div>
                        <VerticalDivider />
                        <div style={{ marginRight: 'auto' }}>
                            <TextDisplaySMMedium>
                                {getBalanceLabel(
                                    company?.companyServiceFee ?? undefined,
                                    company?.currency ?? 'DKK'
                                )}
                            </TextDisplaySMMedium>
                            <TextLGRegularBrand>
                                {' '}
                                Monthly service fee
                            </TextLGRegularBrand>
                        </div>
                        <VerticalDivider />
                        <div style={{ marginRight: 'auto' }}>
                            <TextDisplaySMMedium>
                                {getBalanceLabel(
                                    company?.pricePerUser,
                                    company?.currency ?? 'DKK'
                                )}{' '}
                                per user
                            </TextDisplaySMMedium>
                            <TextLGRegularBrand>
                                {' '}
                                {company?.activeUsersCount} Activated users
                            </TextLGRegularBrand>
                        </div>
                    </StyledCardContent>
                    <div
                        style={{
                            alignSelf: 'start',
                            display: 'flex',
                            flexDirection: 'row',
                            gap: '8px'
                        }}
                    >
                        <TextLGSemiBoldBrand alignSelf={'start'}>
                            Next period starts:
                        </TextLGSemiBoldBrand>
                        <TextLGRegular> {getNextPeriodStart()}</TextLGRegular>
                    </div>
                </StyledCard>
            </Box>
        </>
    );
};
